import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { toast } from "react-toastify";
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faCircleCheck, faClock, faServer, faTriangleExclamation, faTv, faWaveSquare } from "@fortawesome/free-solid-svg-icons";
import AppShell, { PageHeader } from "../components/shell/app-shell";
import Icon3D from "../components/ui/icon-3d";
import StatTile from "../components/ui/stat-tile";
import StatusPill from "../components/ui/status-pill";
import SkeletonList from "../components/ui/skeleton-list";
import { LatencyBadge } from "../components/ui/latency";
import { PanelTitle } from "../components/ui/detail";
import { TONES } from "../components/ui/tones";
import useAutoRefresh from "../hooks/use-auto-refresh";
import { usePlatform } from "../store/use-platform";
import { Overview, PlatformOverview, fetchOverview } from "../utils/api";
import { CHECK_LOOK, HEALTH_LOOK, PLATFORM_LOOK, RUN_LOOK } from "../utils/status";
import { RunStatus } from "../types/job-run";
import { CheckStatus } from "../types/health-check";
import { ParseErrors } from "../utils/ParseErrors";

const hourLabel = (value: string) => new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// ----------------------- PLATFORM CARD ------------------------------------
export function PlatformCard({ item }: { item: PlatformOverview }) {
  const look = PLATFORM_LOOK[item.platform];
  const health = HEALTH_LOOK[item.status];

  return (
    <div className={clsx("glass glass-hover group relative flex flex-col gap-4 overflow-hidden p-5", item.status === "critical" && "border-rose-500/50")}>
      <div className="flex items-center gap-3">
        <Icon3D icon={look.icon} tone={look.tone} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="font-[Gilroy-Bold] text-[17px] text-heading-color">{look.label}</p>
          <p className="font-[Gilroy-Medium] text-[12px] text-label-muted">{item.apiEvents24h.toLocaleString()} API events · 24h</p>
        </div>
        <StatusPill tone={health.tone} live={item.status !== "healthy"}>{health.label}</StatusPill>
      </div>

      <dl className="grid grid-cols-3 gap-3">
        <div>
          <dt className="font-[Gilroy-Medium] text-[11px] text-label-muted">Error rate</dt>
          <dd className={clsx("font-[Gilroy-Bold] text-[20px] tabular-nums", item.errorRate > 5 ? "text-rose-600 dark:text-rose-300" : "text-heading-color")}>{item.errorRate}%</dd>
        </div>
        <Link to="/alerts" className="rounded-lg transition-colors hover:bg-overlay">
          <dt className="font-[Gilroy-Medium] text-[11px] text-label-muted">Open alerts</dt>
          <dd className={clsx("font-[Gilroy-Bold] text-[20px] tabular-nums", item.openAlerts ? "text-rose-600 dark:text-rose-300" : "text-heading-color")}>{item.openAlerts}</dd>
        </Link>
        <Link to="/cron-jobs" className="rounded-lg transition-colors hover:bg-overlay">
          <dt className="font-[Gilroy-Medium] text-[11px] text-label-muted">Cron jobs</dt>
          <dd className={clsx("font-[Gilroy-Bold] text-[20px] tabular-nums", item.jobs.bad ? "text-rose-600 dark:text-rose-300" : "text-heading-color")}>
            {item.jobs.total ? `${item.jobs.total - item.jobs.bad}/${item.jobs.total}` : "—"}
          </dd>
        </Link>
      </dl>

      <div className="flex items-center justify-between gap-3">
        <span className="font-[Gilroy-Medium] text-[12px] text-label-muted">p95 response</span>
        <LatencyBadge ms={item.p95Latency} />
      </div>

      <div>
        <div className="mb-1.5 flex items-center justify-between font-[Gilroy-Medium] text-[11px] text-label-muted">
          <span>Server pulse</span>
          <span>{item.pulse.status ? CHECK_LOOK[item.pulse.status as CheckStatus]?.label : item.pulse.configured ? "Waiting" : "Not set up"}</span>
        </div>
        <div className="flex h-3 gap-[3px]">
          {item.pulse.recent.length ? (
            item.pulse.recent.map((tick) => <span key={tick.createdAt} title={tick.status} className={clsx("flex-1 rounded-[3px]", TONES[CHECK_LOOK[tick.status as CheckStatus]?.tone ?? "slate"].dot)} />)
          ) : (
            <span className="flex-1 rounded-[3px] bg-overlay-mid" />
          )}
        </div>
      </div>
    </div>
  );
}

export default function OverviewPage() {
  const { platform } = usePlatform();
  const [data, setData] = useState<Overview | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchOverview());
    } catch (error) {
      toast.error(ParseErrors(error));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useAutoRefresh(load, 30000);

  const platforms = (data?.platforms ?? []).filter((item) => platform === "all" || item.platform === platform);
  const problems = platforms.filter((item) => item.status !== "healthy" || item.openAlerts || item.jobs.bad);
  const runs = Object.entries(data?.runStatus24h ?? {}).map(([status, value]) => ({ status: status as RunStatus, value }));

  return (
    <AppShell>
      <PageHeader
        title="Overview"
        subtitle="Production health of every Sure platform, refreshed every 30 seconds."
        actions={
          <Link to="/wall" className="inline-flex h-10 items-center gap-2 rounded-xl border border-edge bg-surface px-3.5 font-[Gilroy-Medium] text-[13px] text-heading-color transition-colors hover:border-accent">
            <FontAwesomeIcon icon={faTv} className="text-orange-500" />
            Open status wall
          </Link>
        }
      />

      {!data ? (
        <SkeletonList rows={4} height="h-32" />
      ) : (
        <>
          <div
            className={clsx(
              "relative mb-6 flex items-center gap-4 overflow-hidden rounded-3xl p-5 text-white shadow-overlay sm:p-6",
              problems.length ? "bg-gradient-to-r from-rose-500 via-rose-600 to-orange-500" : "bg-gradient-to-r from-emerald-500 via-teal-500 to-sky-500",
            )}
          >
            <span className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full bg-white/15 blur-2xl" />
            <span className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-[24px] backdrop-blur">
              <span className="pulse-ring absolute inset-2 rounded-2xl text-white/60" />
              <FontAwesomeIcon icon={problems.length ? faTriangleExclamation : faCircleCheck} className="relative" />
            </span>
            <div className="relative">
              <p className="font-[Gilroy-Bold] text-[20px] sm:text-[22px]">
                {problems.length ? `${problems.length} platform${problems.length > 1 ? "s" : ""} need attention` : "Everything is running smoothly"}
              </p>
              <p className="font-[Gilroy-Medium] text-[14px] text-white/85">
                {problems.length ? problems.map((item) => PLATFORM_LOOK[item.platform].label).join(" · ") : "Servers answer, jobs run on time, no open alerts."}
              </p>
            </div>
            {data.totals.openAlerts ? (
              <Link to="/alerts" className="relative ml-auto hidden rounded-xl bg-white/20 px-4 py-2 font-[Gilroy-Medium] text-[13px] backdrop-blur transition-colors hover:bg-white/30 sm:block">
                See {data.totals.openAlerts} alerts →
              </Link>
            ) : null}
          </div>

          <div className="stagger mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile label="Open alerts" value={data.totals.openAlerts} icon={faBell} tone="rose" note="API, cron and health together" />
            <StatTile label="API events · 24h" value={data.totals.apiEvents24h} icon={faWaveSquare} tone="sky" note={`${data.totals.apiErrors24h.toLocaleString()} errors`} />
            <StatTile label="Cron runs · 24h" value={data.totals.runs24h} icon={faClock} tone="violet" note={`${data.totals.jobsFailing} job(s) failing`} />
            <StatTile label="Platforms down" value={data.totals.platformsDown} icon={faServer} tone={data.totals.platformsDown ? "rose" : "emerald"} note={`of ${data.platforms.length} monitored`} />
          </div>

          <div className="mb-6 grid gap-4 xl:grid-cols-3">
            <div className="glass p-5 xl:col-span-2">
              <PanelTitle>API events · last 24 hours</PanelTitle>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.series}>
                    <defs>
                      {(["healthy", "unhealthy", "critical"] as const).map((key) => (
                        <linearGradient key={key} id={`fill-${key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={TONES[HEALTH_LOOK[key].tone].to} stopOpacity={0.45} />
                          <stop offset="100%" stopColor={TONES[HEALTH_LOOK[key].tone].to} stopOpacity={0} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 6" stroke="var(--color-border-edge)" vertical={false} />
                    <XAxis dataKey="timestamp" tickFormatter={hourLabel} tick={{ fontSize: 11, fill: "var(--color-label-muted)" }} axisLine={false} tickLine={false} minTickGap={30} />
                    <YAxis tick={{ fontSize: 11, fill: "var(--color-label-muted)" }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                    <Tooltip labelFormatter={hourLabel} contentStyle={{ borderRadius: 12, border: "1px solid var(--color-border-edge)", background: "var(--color-surface-elevated)", fontSize: 12 }} />
                    {(["healthy", "unhealthy", "critical"] as const).map((key) => (
                      <Area key={key} type="monotone" dataKey={key} name={HEALTH_LOOK[key].label} stackId="1" stroke={TONES[HEALTH_LOOK[key].tone].to} strokeWidth={2} fill={`url(#fill-${key})`} animationDuration={900} />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="glass p-5">
              <PanelTitle>Cron run results · 24h</PanelTitle>
              {runs.length ? (
                <>
                  <div className="relative mt-2 h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={runs} dataKey="value" nameKey="status" innerRadius={58} outerRadius={82} paddingAngle={3} cornerRadius={6} animationDuration={900}>
                          {runs.map((row) => <Cell key={row.status} fill={TONES[RUN_LOOK[row.status]?.tone ?? "slate"].to} stroke="none" />)}
                        </Pie>
                        <Tooltip formatter={(value: number, name: string) => [value, RUN_LOOK[name as RunStatus]?.label ?? name]} contentStyle={{ borderRadius: 12, border: "none", fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <span className="font-[Gilroy-Bold] text-[26px] text-heading-color">{data.totals.runs24h}</span>
                      <span className="font-[Gilroy-Medium] text-[11px] text-label-muted">runs</span>
                    </div>
                  </div>
                  <ul className="mt-2 grid grid-cols-2 gap-2">
                    {runs.map((row) => (
                      <li key={row.status} className="flex items-center gap-2 font-[Gilroy-Medium] text-[12px] text-light-gray">
                        <span className={clsx("h-2.5 w-2.5 rounded-full", TONES[RUN_LOOK[row.status]?.tone ?? "slate"].dot)} />
                        {RUN_LOOK[row.status]?.label ?? row.status} <b className="ml-auto text-heading-color">{row.value}</b>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="flex h-56 flex-col items-center justify-center gap-3 text-center">
                  <Icon3D icon={faClock} tone="violet" size="xl" flat />
                  <p className="font-[Gilroy-Medium] text-[13px] text-light-gray">No cron runs in the last 24 hours.</p>
                </div>
              )}
            </div>
          </div>

          <div className="stagger grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {platforms.map((item) => <PlatformCard key={item.platform} item={item} />)}
          </div>
        </>
      )}
    </AppShell>
  );
}
